import { useEffect, useRef, useState } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { Terminal as TerminalIcon, Wifi, WifiOff } from "lucide-react";

const LEVEL_COLORS = {
  info: "\x1b[37m",
  ok:   "\x1b[32m",
  warn: "\x1b[33m",
  error: "\x1b[31m",
  log:  "\x1b[90m",
  ping: "\x1b[90m",
};
const RESET = "\x1b[0m";
const DIM = "\x1b[90m";
const CYAN = "\x1b[36m";

function fmtLine(line) {
  const at = (line.at || "").slice(11, 19);
  const src = (line.source || "bridge").padEnd(10);
  const color = LEVEL_COLORS[line.level] || LEVEL_COLORS.info;
  const text = (line.text || "").replace(/\r/g, "").replace(/\n/g, "\r\n  ");
  return `${DIM}${at}${RESET} ${CYAN}${src}${RESET} ${color}${text}${RESET}`;
}

function safeWrite(term, line) {
  if (!term) return;
  try { term.writeln(line); } catch {/* xterm renderer may not be ready yet */}
}

function safeFit(fit, container) {
  if (!fit || !container) return;
  // Only fit when the container actually has dimensions and is in the DOM.
  if (!container.isConnected) return;
  const w = container.clientWidth || 0;
  const h = container.clientHeight || 0;
  if (w < 20 || h < 20) return;
  try { fit.fit(); } catch {/* xterm-addon-fit can throw if renderer not initialised */}
}

/**
 * Read-only live terminal that streams job logs over WebSocket.
 *
 * Props:
 *  - jobId, apiBase, token (required)
 */
export default function InvestigationTerminal({ jobId, apiBase, token }) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const pendingRef = useRef([]); // queue of writes until terminal is opened
  const openedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Init xterm — wait one rAF + small delay so the container has settled dimensions
  useEffect(() => {
    if (!containerRef.current || termRef.current) return;
    let cancelled = false;
    let resizeObs = null;

    const setup = () => {
      if (cancelled || !containerRef.current) return;
      const term = new Terminal({
        convertEol: true,
        fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
        fontSize: 12,
        lineHeight: 1.35,
        theme: {
          background: "#0a0a0a",
          foreground: "#e5e5e5",
          cursor: "transparent",
          selectionBackground: "#3b3b3b",
        },
        disableStdin: true,
        cursorBlink: false,
        scrollback: 4000,
        allowProposedApi: true,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(containerRef.current);
      termRef.current = term;
      fitRef.current = fit;
      openedRef.current = true;

      safeFit(fit, containerRef.current);
      safeWrite(term, `${DIM}// ratchet investigation terminal · read-only · streaming from bridge daemon${RESET}`);

      // Flush queued writes that arrived before xterm was ready
      const flush = () => {
        const queue = pendingRef.current;
        pendingRef.current = [];
        for (const ln of queue) safeWrite(term, ln);
      };
      // Run flush after a paint so renderer fully initialised
      requestAnimationFrame(() => { requestAnimationFrame(flush); });

      // ResizeObserver — refit only when container has real dimensions
      try {
        resizeObs = new ResizeObserver(() => safeFit(fit, containerRef.current));
        resizeObs.observe(containerRef.current);
      } catch {/* ResizeObserver may not exist in old browsers */}
    };

    // Two rAFs ensures the parent layout has measured the div
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(setup);
    });

    const onResize = () => safeFit(fitRef.current, containerRef.current);
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      window.removeEventListener("resize", onResize);
      try { resizeObs?.disconnect(); } catch {/* noop */}
      try { termRef.current?.dispose(); } catch {/* noop */}
      termRef.current = null;
      fitRef.current = null;
      openedRef.current = false;
    };
  }, []);

  // WebSocket lifecycle
  useEffect(() => {
    if (!jobId || !apiBase || !token) return;

    let closed = false;
    let reconnectTimer = null;

    const writeOrQueue = (line) => {
      if (termRef.current && openedRef.current) {
        safeWrite(termRef.current, line);
      } else {
        pendingRef.current.push(line);
      }
    };

    const buildWsUrl = () => {
      const wsBase = apiBase.replace(/^http/, "ws").replace(/\/+$/, "");
      return `${wsBase}/api/ws/jobs/${jobId}/logs?token=${encodeURIComponent(token)}`;
    };

    const connect = () => {
      try {
        const ws = new WebSocket(buildWsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          writeOrQueue(`${LEVEL_COLORS.ok}[ws] connected · live tail enabled${RESET}`);
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.level === "ping") return;
            writeOrQueue(fmtLine(data));
          } catch {
            writeOrQueue(String(ev.data));
          }
        };
        ws.onclose = (ev) => {
          setConnected(false);
          if (closed) return;
          writeOrQueue(`${LEVEL_COLORS.warn}[ws] disconnected (code=${ev.code}) — reconnecting in 3s...${RESET}`);
          reconnectTimer = setTimeout(() => {
            setReconnectAttempt((n) => n + 1);
            connect();
          }, 3000);
        };
        ws.onerror = () => {/* onclose follows */};
      } catch (e) {
        writeOrQueue(`${LEVEL_COLORS.error}[ws] failed to open: ${e.message}${RESET}`);
      }
    };
    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch {/* noop */}
    };
  }, [jobId, apiBase, token]);

  return (
    <div className="surface p-0 overflow-hidden" data-testid="investigation-terminal">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2 text-xs font-mono text-white/70">
          <TerminalIcon className="w-3.5 h-3.5" />
          <span>investigation://{jobId?.slice(0, 8) || "—"}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider" data-testid="terminal-ws-status">
          {connected ? (
            <><Wifi className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">live</span></>
          ) : (
            <><WifiOff className="w-3 h-3 text-amber-400" /><span className="text-amber-400">offline{reconnectAttempt > 0 ? ` · retry #${reconnectAttempt}` : ""}</span></>
          )}
        </div>
      </div>
      <div ref={containerRef} className="h-80 bg-[#0a0a0a]" data-testid="terminal-pane" />
    </div>
  );
}
